import React, { useRef } from "react";
import { Box, ButtonWithIcon, GoogleIcon } from "../design-system";
import { colors } from "../design-system/colors";
import { GOOGLE_SIGN_IN_ENABLED, GOOGLE_SIGN_IN_DISABLED_HINT } from "./googleAvailability";
import { GoogleLogin } from "@react-oauth/google";

interface GoogleSignUpButtonProps {
  onSuccess: (credentialResponse: any) => void;
  onError: () => void;
}

export const GoogleSignUpButton: React.FC<GoogleSignUpButtonProps> = ({
  onSuccess,
  onError,
}) => {
  const googleButtonRef = useRef<HTMLDivElement>(null);

  const handleGoogleButtonClick = () => {
    if (!GOOGLE_SIGN_IN_ENABLED) return;
    if (googleButtonRef.current) {
      const button =
        googleButtonRef.current.querySelector<HTMLDivElement>(
          'div[role="button"]',
        );
      if (button) {
        button.click();
      }
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        width: "100%",
        position: "relative",
        "&:hover .MuiButton-root": {
          borderColor: colors.neutral[700],
          backgroundColor: "rgba(255, 255, 255, 0.04)",
          boxShadow: "0 0 7px 0 rgba(255, 255, 255, 0.25) inset",
        },
      }}
    >
      <Box
        ref={googleButtonRef}
        sx={{
          position: "absolute",
          width: "100%",
          height: "50px",
          opacity: 0.0001,
          zIndex: 1,
          // Without a configured client id the hidden Google widget must not be clickable
          // either — otherwise the "disabled" button above is bypassed by the overlay.
          pointerEvents: GOOGLE_SIGN_IN_ENABLED ? "auto" : "none",
          "& > div": {
            width: "100%",
            height: "100%",
          },
          '& div[role="button"]': {
            width: "100%",
            height: "100%",
          },
        }}
      >
        <GoogleLogin
          onSuccess={onSuccess}
          onError={onError}
          shape="circle"
          size="large"
          width="850px"
        />
      </Box>

      <ButtonWithIcon
        variant="outlined"
        icon={<GoogleIcon />}
        iconPosition="left"
        onClick={handleGoogleButtonClick}
        disabled={!GOOGLE_SIGN_IN_ENABLED}
        title={GOOGLE_SIGN_IN_ENABLED ? undefined : GOOGLE_SIGN_IN_DISABLED_HINT}
        fullWidth
        sx={{
          gap: "16px",
        }}
      >
        Sign up with Google
      </ButtonWithIcon>
    </Box>
  );
};
